import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Database } from 'lucide-react';
import { api } from '../../lib/api';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const ORDER = ['requests', 'carts', 'messages', 'newsletter', 'analytics'];

export const DataManagement = () => {
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState('');
    const [confirm, setConfirm] = useState(null); // { key, label, action: 'archive' | 'restore', count }

    const load = useCallback(async () => {
        try {
            const r = await api.get('/admin/data/summary');
            setSummary(r.data);
        } catch { toast.error('Failed to load data summary.'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const run = async () => {
        if (!confirm) return;
        const { key, action } = confirm;
        setBusy(`${key}-${action}`);
        try {
            const r = await api.post(`/admin/data/${key}/${action}`);
            const n = action === 'archive' ? r.data.archived : r.data.restored;
            toast.success(action === 'archive' ? `Archived ${n} record${n === 1 ? '' : 's'}.` : `Restored ${n} record${n === 1 ? '' : 's'}.`);
            setConfirm(null);
            await load();
        } catch { toast.error('Action failed.'); }
        finally { setBusy(''); }
    };

    return (
        <section className="luxury-card p-6 space-y-4 lg:col-span-2" data-testid="admin-data-management-section">
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--brand-rose)' }}><Database className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} /></div>
                <div>
                    <h2 className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Data Management</h2>
                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Archive test data to hide it from the panel and analytics. Archiving is reversible — restore any type anytime. Nothing is permanently deleted.</p>
                </div>
            </div>

            {!summary ? (
                <p className="text-sm py-4" style={{ color: 'var(--ink-muted)' }}>Loading...</p>
            ) : (
                <div className="divide-y divide-[rgba(90,36,50,0.08)] border border-[rgba(90,36,50,0.10)] rounded-xl overflow-hidden">
                    {ORDER.filter((k) => summary[k]).map((key) => {
                        const row = summary[key];
                        return (
                            <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`admin-data-row-${key}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-serif text-lg" style={{ color: 'var(--brand-burgundy)' }}>{row.label}</p>
                                    <p className="text-xs" style={{ color: 'var(--ink-muted)' }} data-testid={`admin-data-count-${key}`}>
                                        {row.active} active{row.archived > 0 ? ` · ${row.archived} archived` : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {row.archived > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setConfirm({ key, label: row.label, action: 'restore', count: row.archived })}
                                            disabled={busy === `${key}-restore`}
                                            data-testid={`admin-data-restore-${key}`}
                                            className="btn-outline-burgundy px-3 py-2 rounded-lg text-xs uppercase tracking-[0.14em] inline-flex items-center gap-2 disabled:opacity-60"
                                        >
                                            <ArchiveRestore className="w-4 h-4" /> Restore
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setConfirm({ key, label: row.label, action: 'archive', count: row.active })}
                                        disabled={row.active === 0 || busy === `${key}-archive`}
                                        data-testid={`admin-data-archive-${key}`}
                                        className="btn-burgundy px-3 py-2 rounded-lg text-xs uppercase tracking-[0.14em] inline-flex items-center gap-2 disabled:opacity-40"
                                    >
                                        <Archive className="w-4 h-4" /> Archive All
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
                <AlertDialogContent data-testid="admin-data-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirm?.action === 'archive' ? `Archive all ${confirm?.label}?` : `Restore ${confirm?.label}?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirm?.action === 'archive'
                                ? `This will hide ${confirm?.count} record${confirm?.count === 1 ? '' : 's'} from the admin panel and analytics. You can restore them later — nothing is permanently deleted.`
                                : `This will bring back ${confirm?.count} archived record${confirm?.count === 1 ? '' : 's'} into the admin panel and analytics.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-data-confirm-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={run} data-testid="admin-data-confirm-run" className="btn-burgundy">
                            {confirm?.action === 'archive' ? 'Archive All' : 'Restore'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
};
